// Méthodes générales (show/hide/css/listeners) communes à tous les composants tbsComponents.
// Repose sur un attribut data-tbs-root={id} posé sur le conteneur racine de chaque composant.

import { toCssLength } from '@/lib/utils'

// Stockage module-level des listeners : Map<id, { eventName: fn[] }>
const _listeners = new Map()

// Appelé au démontage d'un composant pour libérer ses listeners
export function clearListeners(id) {
  _listeners.delete(id)
}

export function createGeneralApi(id, userData) {
  const getEl = () => document.querySelector(`[data-tbs-root="${id}"]`)
  // En layout grille (ScreenGenerator.jsx, renderGrid), chaque composant est enveloppé dans un
  // wrapper dédié (padding) portant data-tbs-cell-wrapper={id} — ciblé par les méthodes de
  // visibilité (show/hide/setVisible/isVisible) EN PLUS du composant lui-même, pas à sa place :
  // sans le wrapper, hide() ne masquait que le div racine du composant (LabelField/[data-tbs-root])
  // — le wrapper, parent de ce div racine, restait visible avec son padding, un espace fantôme à
  // la place du composant caché. Mais se limiter au seul wrapper est tout aussi incorrect : un
  // composant initialement `visible: false` dans le JSON pose lui-même `display:none` sur son
  // propre div racine (voir `visibilityStyle` dans chaque composant, ex. ChampField.jsx) —
  // show() ne levant alors que le display:none du wrapper laissait le composant cité toujours
  // caché par son propre style inline. D'où l'union des deux éléments (racine + wrapper, dédoublonnés
  // via Set — un seul en dehors d'un layout grille, où il n'existe pas de wrapper).
  const getVisibilityEls = () => {
    const els = new Set()
    const root = getEl()
    if (root) els.add(root)
    const wrapper = document.querySelector(`[data-tbs-cell-wrapper="${id}"]`)
    if (wrapper) els.add(wrapper)
    return [...els]
  }

  const show = () => { for (const el of getVisibilityEls()) el.style.display = '' }
  const hide = () => { for (const el of getVisibilityEls()) el.style.display = 'none' }

  // Métadonnée opaque, non affichée : une simple variable de closure suffit (pas de re-rendu à
  // déclencher), sur le même principe que compoGroup (initialisée depuis l'attribut JSON, mais
  // pas resynchronisée si le composant se re-rend avec un nouvel attribut `userData`).
  let currentUserData = userData ?? null

  return {
    getId: () => id,
    getUserData: () => currentUserData,
    setUserData: v => { currentUserData = v },
    show,
    hide,
    setVisible: visible => (visible ? show() : hide()),
    isVisible: () => {
      const els = getVisibilityEls()
      return els.length > 0 && els.every(el => getComputedStyle(el).display !== 'none')
    },
    // Accepte soit des paires (clé, valeur, ...) soit un objet JSON { clé: valeur }
    addCss: (...args) => {
      const el = getEl()
      if (!el) return
      if (args.length === 1 && args[0] !== null && typeof args[0] === 'object') {
        for (const [k, v] of Object.entries(args[0])) el.style.setProperty(k, v)
      } else {
        for (let i = 0; i < args.length - 1; i += 2) el.style.setProperty(args[i], args[i + 1])
      }
    },
    getCss: cssStyleName => {
      const el = getEl()
      return el ? getComputedStyle(el).getPropertyValue(cssStyleName) : undefined
    },
    // width/height : entier ou chaîne numérique sans unité → pixels ; chaîne avec unité CSS
    // explicite ("10em", "50%"...) → conservée telle quelle (même normalisation que fixedHeight,
    // via toCssLength()). Les getters rendent toujours la taille rendue, en pixels entiers.
    setWidth:  width  => { const el = getEl(); if (el) el.style.width  = toCssLength(width) },
    getWidth:  ()      => { const el = getEl(); return el ? el.offsetWidth : undefined },
    setHeight: height => { const el = getEl(); if (el) el.style.height = toCssLength(height) },
    getHeight: ()      => { const el = getEl(); return el ? el.offsetHeight : undefined },
    // Accepte un ou plusieurs noms CSS (variadic)
    removeCss: (...cssNames) => {
      const el = getEl()
      if (!el) return
      for (const name of cssNames) el.style.removeProperty(name)
    },
    // Accepte une chaîne de noms de classes séparées par des espaces
    addClassCss: cssClassName => {
      const el = getEl()
      if (!el) return
      for (const cls of String(cssClassName).split(' ').filter(Boolean)) el.classList.add(cls)
    },
    removeClassCss: cssClassName => {
      const el = getEl()
      if (!el) return
      for (const cls of String(cssClassName).split(' ').filter(Boolean)) el.classList.remove(cls)
    },
    // Ajoute un listener sur un event — toujours appelé même si onTbsEventCallback retourne false
    addListener: (event, fn) => {
      if (!_listeners.has(id)) _listeners.set(id, {})
      const map = _listeners.get(id)
      if (!map[event]) map[event] = []
      map[event].push(fn)
    },
    // Retire un listener posé par addListener (même référence de fonction) — no-op si absent
    removeListener: (event, fn) => {
      const map = _listeners.get(id)
      if (!map?.[event]) return
      map[event] = map[event].filter(f => f !== fn)
    },
    // Méthode interne appelée par App.jsx après onTbsEventCallback
    _callListeners: (event, ...args) => {
      const map = _listeners.get(id)
      if (!map) return
      for (const fn of (map[event] ?? [])) fn(id, event, ...args)
    },
  }
}
